import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { EditPencil } from 'iconoir-react'
import { isEqual } from 'lodash'
import { MouseEventHandler, useEffect, useRef, useState } from 'react'
import { useControlField, useField } from 'remix-validated-form'
import { twMerge } from 'tailwind-merge'
import FormComboAsyncV2, { AsyncComboboxProps } from '../form/FormComboAsyncV2'
import FormDatePicker from '../form/FormDatePicker'
import FormInput, { FormInputProps } from '../form/FormInput'
import FormInputWrapper from '../form/FormInputWrapper'
import FormPhone from '../form/FormPhone'
import { Info } from '../Info'
import { CopyToClipboard } from '../shared/CopyToClipboard'
import { Tooltip } from '../Tooltip'
import { LockIcon } from './LockIcon'
import Combo from 'app/components/ui/combo/combo'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'
import { createGqlFetcher } from 'app/lib/gqlFetcher'

export const wrapperClassName = `flex w-full items-center justify-between gap-x-2 pt-1.5 align-middle text-strong`
export const labelClass = `!mb-0 w-36 capitalize !text-ui-xs text-strong`
export const inputClass = `!h-[30px] !w-44 rounded-lg border !border-strong bg-surface !py-1 !px-2 !shadow-elevation-1 focus:text-body-sm focus:text-strong focus:!shadow-elevation-1 sm:text-body-sm sm:text-strong`
export const dropdownInputClass = `!h-[30px] !w-44 rounded-lg border !border-strong !pl-2 !pr-8 !shadow-elevation-1 sm:text-body-sm sm:text-strong`

// isArray, arrayFieldName, arrayIndex are used to handle array fields in CallDrawer
// It is currently commented out because it is not used in the current implementation
// If CallDrawer is removed please remove isArray, arrayFieldName, arrayIndex from TypedFormField
export type TypedFormField<T> = {
  field: Extract<keyof T, string> | `isQualifiedLead` | `isMarkedNonPatient`
  required?: boolean
  options?: OptionType[]
  readonly?: boolean
  nullable?: boolean
  onClick?: (evt: MouseEventHandler<HTMLInputElement>) => void
  isLocked?: boolean
  useSingleFieldInput?: boolean
} & (
  | {
      isArray: true
      arrayFieldName: string
      arrayIndex: number
    }
  | {
      isArray?: false
      arrayFieldName?: string
      arrayIndex?: number
    }
)

export function EditableFormField({
  label,
  name,
  value,
  infoTooltip,
  canCopy,
  disabled,
  className,
}: {
  label: string
  name: string
  value: string | undefined
  infoTooltip?: { title: string; message: string }
  isDate?: boolean
  isLocked?: boolean
  canCopy?: boolean
  disabled?: boolean
  className?: string
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const { setTouched } = useField(name)
  const ref = useRef<HTMLInputElement>(null)
  useEscapeListener(() => {
    setEditValue(value)
    setEditing(false)
  }, editing)

  useEffect(() => {
    if (editing) {
      ref.current?.focus()
      // highlight the text in the input field
      ref.current?.setSelectionRange(0, ref.current.value.length)
    }
  }, [editing])

  useEffect(() => {
    if (editing) return
    setTouched(editValue !== value)
  }, [editValue, editing])

  return (
    <div className={twMerge(`pt-0`, wrapperClassName)} x-component-name="EditableFormField">
      <div className="inline-flex max-w-[160px] shrink-0 items-center gap-x-2">
        <span className="items-center text-ui-xs text-strong">{label}</span>
        {infoTooltip && <Info title={infoTooltip.title} message={infoTooltip.message} />}
      </div>
      <button
        className={`inline-flex h-[30px] w-44 items-center justify-end rounded-md border px-2 py-0.5 ${
          editing ? `border-blue-600` : `border-strong`
        } ${disabled ? `border-0` : `rounded-md border`}`}
        style={editing ? { backgroundColor: `var(--primary-alpha-8)` } : undefined}
        onClick={(evt) => {
          setEditing((prev) => {
            if (prev === false) return true
            return prev
          })
        }}
      >
        {editing ? (
          <>
            <input
              ref={ref}
              className="h-[20px] w-full bg-transparent text-left sm:text-body-sm sm:text-strong"
              name={name}
              value={editValue ?? ``}
              onKeyDown={(e) => {
                if (e.key === `Enter`) {
                  e.preventDefault()
                  e.stopPropagation()
                  setEditing(false)
                }
              }}
              onChange={(e) => {
                setEditValue(e.target.value)
              }}
              onBlur={(e) => {
                setEditing(false)
              }}
            />
            <div className="ml-1 flex shrink-0 items-center truncate bg-surface">
              <div
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-surface-subtle p-1"
                onMouseDown={(e) => {
                  e.preventDefault()
                  setEditValue(value)
                  setEditing(false)
                }}
              >
                <XMarkIcon className="stroke-current text-strong" />
              </div>
              <div
                className="prevent-blur prevent-blur ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border bg-blue-600 p-1"
                onClick={() => setEditing(false)}
              >
                <CheckIcon className="stroke-current text-on-color" />
              </div>
            </div>
          </>
        ) : (
          <>
            <input type="hidden" name={name} value={editValue} />
            <Tooltip message={editValue} className="w-full !cursor-pointer truncate">
              <div className="truncate border-0 bg-surface text-left sm:text-body-sm sm:text-strong">
                <span className={className} onClick={() => setEditing((prev) => !prev)}>
                  {editValue}
                </span>
              </div>
            </Tooltip>
            {canCopy && (
              <div className="mt-0.5 h-3.5 w-3.5 pr-2.5">
                <CopyToClipboard text={value ?? ``} />
              </div>
            )}
            {!disabled && <EditPencil className="h-3 w-4" onClick={() => setEditing(true)} />}
          </>
        )}
      </button>
    </div>
  )
}
export function TypedReadOnly({
  label,
  labelClassName,
  value,
  infoTooltip,
  isDate,
  isPhone,
  isLocked,
  canCopy,
  className,
  onEdit,
}: {
  label: string
  labelClassName?: string
  value: any
  infoTooltip?: { title: string; message: string }
  isDate?: boolean
  isPhone?: boolean
  isLocked?: boolean
  canCopy?: boolean
  className?: string
  onEdit?: () => void
}) {
  // Convert value to string for display and tooltip
  const displayValue = isDate
    ? formatDate({ value: value, dateOnly: true })
    : isPhone
      ? formatPhoneNumber(value)
      : typeof value === `object` && value !== null
        ? JSON.stringify(value)
        : String(value || ``)

  let [tooltipContent, setTooltipContent] = useState<string | undefined>(displayValue)

  return (
    <div className={wrapperClassName}>
      <div className="inline-flex max-w-[160px] items-center gap-x-2">
        <span className={classNames(`grow items-center text-ui-xs text-strong`, labelClassName)}>{label}</span>
        {infoTooltip && <Info title={infoTooltip.title} message={infoTooltip.message} />}
        {isLocked && <LockIcon />}
      </div>
      <div className="inline-flex min-w-0 items-center space-x-1 align-middle">
        <Tooltip
          className="min-w-0 flex-1"
          message={tooltipContent}
          tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
        >
          <div
            className="max-w-40 truncate border-0 bg-surface sm:text-body-sm sm:text-strong"
            onMouseOver={(event) => {
              const target = event.currentTarget
              const isOverflowing = target instanceof HTMLElement && target.scrollWidth > target.clientWidth
              setTooltipContent(isOverflowing ? displayValue : undefined)
            }}
          >
            <span className={classNames(`ml-2 overflow-hidden text-ellipsis align-middle`, className)}>
              {displayValue}
            </span>
          </div>
        </Tooltip>
        {canCopy && value && (
          <div className="mt-0.5 h-3.5 w-3.5 pr-2.5">
            <CopyToClipboard text={displayValue || ``} />
          </div>
        )}
      </div>
    </div>
  )
}

export function TypedFormInput<T>(props: Omit<FormInputProps, `name`> & TypedFormField<T>) {
  const { isArray, arrayFieldName, arrayIndex, required, ...rest } = props
  let name = isArray ? `${arrayFieldName}[${arrayIndex}].${props.field}` : props.field
  const { defaultValue, setTouched } = useField(name)
  const handleChange = (evt) => setTouched(evt.target.value !== (defaultValue ?? ``))
  return (
    <FormInput
      {...rest}
      onChange={handleChange}
      label={props.label}
      name={name}
      wrapperClassName={wrapperClassName}
      labelClassName={classNames(labelClass, { [`required`]: required })}
      className={inputClass}
      errorClassName="!text-ui-xs"
      cols={6}
      newLineError={false}
      showErrorIcon
    />
  )
}

export function TypedPhoneFormInput<T>(props: Omit<FormInputProps, `name`> & TypedFormField<T>) {
  const { isArray, arrayFieldName, arrayIndex, required, ...rest } = props
  let name = isArray ? `${arrayFieldName}[${arrayIndex}].${props.field}` : props.field
  const { defaultValue, setTouched } = useField(name)
  const handleChange = (evt) => setTouched(evt.target.value !== (defaultValue ?? ``))
  return (
    <FormPhone
      {...rest}
      onChange={handleChange}
      name={name}
      wrapperClassName={wrapperClassName}
      labelClassName={classNames(labelClass, { [`required`]: required })}
      className={inputClass}
      errorClassName="!text-ui-xs"
      cols={6}
      newLineError={false}
      showErrorIcon
    />
  )
}

export function TypedFormDate<T>(
  props: Omit<FormInputProps, `name`> & TypedFormField<T> & { allowPastDates?: boolean },
) {
  const { getInputProps, setTouched } = useField(props.field)
  const { isArray, arrayFieldName, arrayIndex, ...rest } = props
  let { defaultValue } = getInputProps({ ...rest })
  let inputDate = defaultValue ? formatDate({ value: defaultValue, dateOnly: true }) : ``

  return (
    <FormDatePicker
      cols={6}
      {...getInputProps({
        ...props,
        value: inputDate,
        onChange: (val) => setTouched(val !== (defaultValue ?? ``)),
      })}
      disabled={props.readOnly}
      name={isArray ? `${arrayFieldName}[${arrayIndex}].${props.field}` : props.field}
      wrapperClassName={wrapperClassName}
      labelClassName={labelClass}
      className={classNames(`h-8`, inputClass)}
      allowPastDates={props.allowPastDates}
    />
  )
}

export function TypedSelectForm<T>(props: Omit<FormInputProps, `name`> & TypedFormField<T>) {
  const { isArray, arrayFieldName, arrayIndex, required } = props
  let name = isArray ? `${arrayFieldName}[${arrayIndex}].${props.field}` : props.field
  const { setTouched, defaultValue, error } = useField(name)
  const [value, setValue] = useControlField<string[]>(name)

  useEffect(() => {
    debug(`contacts`, `value changed`, name, value, typeof value)
  }, [value])

  return props.options && props.options?.length > 0 ? (
    <FormInputWrapper
      {...{
        name,
        error,
        label: props.label,
        wrapperClassName: classNames(
          wrapperClassName,
          `h-7 !py-0`,
          (props.disabled || props.readOnly) && `!cursor-not-allowed`,
        ),
        className: classNames(labelClass, { [`required`]: required }),
        newLineError: false,
        showErrorIcon: true,
        errorClassName: `!text-ui-xs`,
        cols: 6,
        infoTooltip: props.infoTooltip,
      }}
    >
      <Combo
        useSingleFieldInput={props.useSingleFieldInput}
        inputClassName={classNames(
          `!h-7 text-body-sm text-strong`,
          `disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-subtle`,
          dropdownInputClass,
        )}
        name={name}
        placeholder=""
        value={
          props.options.find((option) => option.value === value) ||
          props.options.find((option) => option.value === props.value) ||
          props.options.find((option) => option.value === defaultValue) ||
          props.options.find((option) => option.value === props.defaultValue)
        }
        options={props.options ?? []}
        onChange={(option) => {
          debug(`contacts`, `TypedSelectForm onChange`, name, option)
          setValue(option?.value)
          setTouched(option.value !== (defaultValue ?? ``))
        }}
        onClearSearch={() => setTouched(!!defaultValue)}
        disabled={props.disabled || props.readOnly}
      />
    </FormInputWrapper>
  ) : null
}

export function TypedAsyncSelectForm<T>(
  props: Omit<Partial<AsyncComboboxProps>, `name`> &
    TypedFormField<T> & {
      locationId: string
      workspaceId: string
      queryURL?: string
      isMarkedNonPatient?: boolean
      default?: OptionType
    },
) {
  const { isArray, arrayFieldName, arrayIndex } = props
  // Array rows have to be namespaced like every other Typed*Form field, or the
  // value submits outside the array its schema expects it in.
  const name = isArray ? `${arrayFieldName}[${arrayIndex}].${props.field}` : props.field
  const { setTouched } = useField(name)
  const [selectedValue, setSelectedValue] = useState<OptionType>(
    props.default || ({ id: ``, name: ``, value: `` } as OptionType),
  )
  const isContactOwner = !props.label
  return (
    <FormComboAsyncV2
      cols={6}
      label={props.label ?? `Contact Owner`}
      name={name}
      wrapperClassName={classNames(wrapperClassName, `!py-0`)}
      inputClassName={classNames(
        dropdownInputClass,
        `disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-subtle`,
      )}
      labelClassName={classNames(labelClass, { [`required`]: props.required })}
      disabled={props.readOnly}
      optionsMapper={(res) => res?.hits?.map((hit) => ({ id: hit.id || hit.name, name: hit.name, value: hit.name }))}
      value={selectedValue}
      readOnly={props.isMarkedNonPatient}
      placeholder=""
      fetcher={props.fetcher ?? createGqlFetcher('users')}
      additionalParams={{
        ...(props.locationId && { locationId: props.locationId },
        props.workspaceId && { workspaceId: props.workspaceId }),
      }}
      onChange={(value) => {
        const val = value.id === selectedValue?.id ? { id: `-1`, name: ``, value: `` } : value
        setTouched(!isEqual(val, props.default))
        setSelectedValue(val)
      }}
      infoTooltip={
        isContactOwner
          ? {
              title: `Contact Owner`,
              message: `The front office / administrative team member who is responsible for scheduling and managing communications with this contact. This field is set by a recent activity or manually by a user.`,
            }
          : undefined
      }
      showSearchInDropdown
      newLineError={false}
      allowDeselectOption
      showErrorIcon
    />
  )
}

export function TypedDateTimePicker<T>(props: Omit<FormInputProps, `name`> & TypedFormField<T>) {
  const { isArray, arrayFieldName, arrayIndex } = props
  let name = isArray ? `${arrayFieldName}[${arrayIndex}].${props.field}` : props.field
  const { defaultValue, setTouched } = useField(name)
  return (
    <FormInputWrapper
      {...{
        name: name,
        label: props.label,
        wrapperClassName: wrapperClassName,
        className: labelClass,
        cols: 6,
      }}
    >
      <input
        type="datetime-local"
        name={name}
        defaultValue={defaultValue}
        onChange={() => setTouched(true)}
        className={classNames(
          inputClass,
          `select-none disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-subtle `,
        )}
      />
    </FormInputWrapper>
  )
}
